/**
 * Control Registry Implementation
 * Manages registration and discovery of form control plugins
 * Includes validation, safety checks, and comprehensive error handling
 */

import type {
  FormControlType,
  ControlPlugin,
  IControlRegistry,
  RegistryObserver,
  GroupedPlugins,
  RegistryEvent,
} from '@atlas-forms/types-js';
import { getAtlasLogger } from '@atlas-forms/types-js';
import { ControlValidator } from './control.validator';
import { ValidationError } from './validation-error';

/**
 * Registration options
 */
export interface RegistrationOptions {
  /** Allow override of existing control type with warning */
  override?: boolean;

  /** Skip validation (not recommended) */
  skipValidation?: boolean;

  /** Treat warnings as errors (strict mode) */
  strict?: boolean;
}

/**
 * Default implementation of the Control Registry
 * Singleton pattern for global control type registration
 * Includes comprehensive validation and safety checks
 */
export class DefaultControlRegistry implements IControlRegistry {
  private plugins: Map<FormControlType, ControlPlugin>;
  private observers: Set<RegistryObserver>;
  private eventBus: RegistryEventBus;
  private deprecatedControls: Set<FormControlType>;

  constructor() {
    this.plugins = new Map();
    this.observers = new Set();
    this.eventBus = new RegistryEventBus();
    this.deprecatedControls = new Set();
  }

  /**
   * Register a new control plugin with validation
   * @param plugin - The control plugin to register
   * @param options - Registration options (override, validation, etc.)
   * @throws ValidationError if control plugin is invalid
   * @throws Error if control type is already registered and override is false
   *
   * @example
   * registry.register({ type: 'text', label: 'Text', group: 'Input' });
   *
   * @example
   * // Override existing control with warning
   * registry.register(plugin, { override: true });
   */
  register(plugin: ControlPlugin, options?: RegistrationOptions): void {
    const opts: RegistrationOptions = {
      override: false,
      skipValidation: false,
      strict: false,
      ...options,
    };

    // Validate plugin unless explicitly skipped
    if (!opts.skipValidation) {
      try {
        ControlValidator.validateControl(plugin);
      } catch (error) {
        if (error instanceof ValidationError) {
          throw error;
        }
        throw new ValidationError(
          `Unexpected validation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          [],
          plugin.type
        );
      }
    }

    // Check for duplicates
    const isDuplicate = this.plugins.has(plugin.type);
    if (isDuplicate && !opts.override) {
      throw new Error(
        `Control type '${plugin.type}' is already registered. Use override=true to replace it.`
      );
    }

    if (isDuplicate && opts.override) {
      getAtlasLogger().warn(`[Registry] Overriding existing control type '${plugin.type}' with new implementation`);
    }

    this.plugins.set(plugin.type, plugin);
    this.notifyObservers({
      type: 'registered',
      pluginType: plugin.type,
      plugin,
    });
    this.eventBus.emit('registered', plugin);
  }

  /**
   * Unregister a control plugin
   * @param type - The control type to unregister
   * @throws Error if control type is not registered
   */
  unregister(type: FormControlType): void {
    if (!this.plugins.has(type)) {
      throw new Error(`Control type '${type}' is not registered`);
    }

    const plugin = this.plugins.get(type)!;
    this.plugins.delete(type);
    this.notifyObservers({ type: 'unregistered', pluginType: type });
    this.eventBus.emit('unregistered', plugin);
  }

  /**
   * Mark a control type as deprecated
   * Will trigger warnings when accessed
   * @param type - The control type to deprecate
   * @param replacement - Optional replacement control type
   */
  deprecate(type: FormControlType, replacement?: FormControlType): void {
    if (!this.plugins.has(type)) {
      getAtlasLogger().warn(`[Registry] Cannot deprecate control type '${type}': not registered`);
      return;
    }

    this.deprecatedControls.add(type);
    const msg = `Control type '${type}' is deprecated`;
    const fullMsg = replacement
      ? `${msg}. Use '${replacement}' instead.`
      : msg;
    getAtlasLogger().warn(`[Registry] ${fullMsg}`);
  }

  /**
   * Check if a control type is deprecated
   * @param type - The control type to check
   * @returns true if deprecated
   */
  isDeprecated(type: FormControlType): boolean {
    return this.deprecatedControls.has(type);
  }

  /**
   * Get a registered control plugin by type
   * Logs warning if control is deprecated
   * @param type - The control type
   * @returns The control plugin or undefined if not found
   */
  get(type: FormControlType): ControlPlugin | undefined {
    const plugin = this.plugins.get(type);

    if (plugin && this.isDeprecated(type)) {
      getAtlasLogger().warn(`[Registry] Control type '${type}' is deprecated`);
    }

    return plugin;
  }

  /**
   * Get all registered plugins
   * @returns Array of all registered plugins
   */
  getAll(): ControlPlugin[] {
    return Array.from(this.plugins.values());
  }

  /**
   * Get plugins grouped by category
   * @returns Grouped plugins organized by category
   */
  getGroups(): GroupedPlugins {
    const grouped: GroupedPlugins = {};

    for (const plugin of this.plugins.values()) {
      const category = plugin.group ?? plugin.category ?? 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(plugin);
    }

    return grouped;
  }

  /**
   * Get plugins in a specific group
   * @param groupLabel - The group name
   * @returns Array of plugins in the group
   */
  getGroupPlugins(groupLabel: string): ControlPlugin[] {
    const groups = this.getGroups();
    return groups[groupLabel] || [];
  }

  /**
   * Check if a control type is registered
   * @param type - The control type
   * @returns True if registered, false otherwise
   */
  has(type: FormControlType): boolean {
    return this.plugins.has(type);
  }

  /**
   * Get total count of registered controls
   * @returns Number of registered controls
   */
  count(): number {
    return this.plugins.size;
  }

  /**
   * Subscribe to registry changes
   * @param observer - The observer to notify on changes
   * @returns Unsubscribe function
   */
  subscribe(observer: RegistryObserver): () => void {
    this.observers.add(observer);
    return () => {
      this.observers.delete(observer);
    };
  }

  /**
   * Listen to registry events
   * @param event - Event type to listen for
   * @param handler - Handler function
   * @returns Unsubscribe function
   */
  on(event: string, handler: (plugin: ControlPlugin) => void): () => void {
    return this.eventBus.on(event, handler);
  }

  /**
   * Validate a control plugin without registering it
   * @param plugin - The control plugin to validate
   * @returns ValidationResult with detailed information
   *
   * @example
   * const result = registry.validatePlugin(myPlugin);
   * if (!result.valid) {
   *   console.error(result.error?.formatDetails());
   * }
   */
  validatePlugin(plugin: ControlPlugin): ValidationResult {
    try {
      ControlValidator.validateControl(plugin);
      return { valid: true };
    } catch (error) {
      if (error instanceof ValidationError) {
        return { valid: false, error };
      }
      return {
        valid: false,
        error: new ValidationError(
          `Unexpected validation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          [],
          plugin.type
        ),
      };
    }
  }

  /**
   * Get validation report for all registered controls
   * @returns Report of any validation issues
   */
  getValidationReport(): ValidationReport {
    const report: ValidationReport = {
      totalControls: this.plugins.size,
      validControls: 0,
      invalidControls: [],
      deprecatedControls: Array.from(this.deprecatedControls),
      warnings: [],
    };

    for (const plugin of this.plugins.values()) {
      const result = this.validatePlugin(plugin);
      if (result.valid) {
        report.validControls++;
      } else if (result.error) {
        report.invalidControls.push({
          type: plugin.type,
          errors: result.error.getDetailsBySeverity('error'),
          warnings: result.error.getDetailsBySeverity('warning'),
        });

        if (result.error.getDetailsBySeverity('warning').length > 0) {
          report.warnings.push({
            type: plugin.type,
            messages: result.error
              .getDetailsBySeverity('warning')
              .map((w) => w.message),
          });
        }
      }
    }

    return report;
  }

  /**
   * Clear all registered plugins
   * Warning: This is destructive and should only be used in tests
   */
  clear(): void {
    this.plugins.clear();
    this.observers.clear();
    this.eventBus.clear();
    this.deprecatedControls.clear();
  }

  /**
   * Notify all observers of registry changes
   */
  public notifyObservers(event: RegistryEvent): void {
    for (const observer of this.observers) {
      try {
        observer.onRegistryChanged(event);
      } catch (error) {
        getAtlasLogger().error('[Registry] Observer notification failed', error);
      }
    }
  }
}

/**
 * Event bus for registry events
 * Allows listening to specific events without observer pattern
 */
class RegistryEventBus {
  private listeners: Map<string, Set<(plugin: ControlPlugin) => void>>;

  constructor() {
    this.listeners = new Map();
  }

  /**
   * Register an event listener
   */
  on(event: string, handler: (plugin: ControlPlugin) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(handler);
    };
  }

  /**
   * Emit an event to all listeners
   */
  emit(event: string, plugin: ControlPlugin): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(plugin);
        } catch (error) {
          getAtlasLogger().error(`[Registry] Event handler failed for '${event}'`, error);
        }
      }
    }
  }

  /**
   * Clear all listeners
   */
  clear(): void {
    this.listeners.clear();
  }
}

/**
 * Result of control plugin validation
 */
export interface ValidationResult {
  /** Whether validation passed */
  valid: boolean;

  /** Validation error if validation failed */
  error?: ValidationError;
}

/**
 * Report of validation issues for all controls
 */
export interface ValidationReport {
  /** Total number of registered controls */
  totalControls: number;

  /** Number of valid controls */
  validControls: number;

  /** Invalid controls with details */
  invalidControls: Array<{
    type: FormControlType;
    errors: Array<{ field: string; message: string; code?: string }>;
    warnings: Array<{ field: string; message: string; code?: string }>;
  }>;

  /** List of deprecated control types */
  deprecatedControls: FormControlType[];

  /** Warning details */
  warnings: Array<{
    type: FormControlType;
    messages: string[];
  }>;
}

/**
 * Global singleton instance of the registry
 */
let globalRegistry: DefaultControlRegistry | null = null;

/**
 * Get the global registry instance
 * Creates one if it doesn't exist
 */
export function getGlobalRegistry(): DefaultControlRegistry {
  if (!globalRegistry) {
    globalRegistry = new DefaultControlRegistry();
  }
  return globalRegistry;
}

/**
 * Reset the global registry (for testing)
 */
export function resetGlobalRegistry(): void {
  globalRegistry = null;
}
